/**
 * ImageUploader.js
 * Reusable image upload component with preview.
 * Converts to base64 and calls onUpload(base64string).
 * No backend dependency — caller handles the API call.
 */
import React, { useRef, useState } from 'react';

export default function ImageUploader({
  currentUrl,       // existing image URL to preview
  onUpload,         // async fn(base64) — caller sends to API
  label = 'Upload Image',
  hint  = 'JPG, PNG or WebP, max 2MB',
  size  = 80,       // preview circle size in px
  shape = 'circle', // 'circle' | 'rect'
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(currentUrl || null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Only image files are allowed'); return; }
    if (file.size > 2 * 1024 * 1024) { setError('Image must be under 2 MB'); return; }
    setError(null);
    setLoading(true);
    try {
      const base64 = await toBase64(file);
      setPreview(base64);
      await onUpload(base64);
    } catch (err) {
      setError('Upload failed. Please try again.');
    } finally { setLoading(false); }
  };

  const toBase64 = (file) => new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload  = () => res(reader.result);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const borderRadius = shape === 'circle' ? size : 10;

  return (
    <div>
      <div
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={onDrop}
        style={{
          width:  size,
          height: size,
          borderRadius,
          border: `2px dashed ${loading ? 'var(--accent-primary)' : 'var(--border)'}`,
          cursor: disabled ? 'not-allowed' : 'pointer',
          position: 'relative',
          overflow: 'hidden',
          background: 'var(--bg-elevated)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'border-color 0.2s',
        }}
      >
        {preview ? (
          <img src={preview} alt="preview"
            style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
        ) : (
          <div style={{ textAlign:'center', padding:8 }}>
            <div style={{ fontSize: size > 60 ? 24 : 16, marginBottom:4 }}>📷</div>
            {size > 60 && <div style={{ fontSize:10, color:'var(--text-muted)', lineHeight:1.3 }}>{label}</div>}
          </div>
        )}
        {/* Loading overlay */}
        {loading && (
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <div className="spinner" style={{ width:24, height:24, borderWidth:3, borderColor:'rgba(255,255,255,0.3)', borderTopColor:'#fff' }} />
          </div>
        )}
        {/* Edit badge */}
        {!disabled && !loading && (
          <div style={{ position:'absolute', bottom:4, right:4, width:20, height:20, borderRadius:10, background:'var(--accent-primary)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, color:'#fff' }}>
            ✎
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" style={{ display:'none' }}
        onChange={e => handleFile(e.target.files?.[0])} />
      {hint && <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:5, maxWidth:size + 40 }}>{hint}</div>}
      {error && <div style={{ fontSize:11, color:'var(--accent-danger)', marginTop:4 }}>{error}</div>}
    </div>
  );
}
